/**
 * variation-thumbnails.js
 *
 * Handles thumbnail-based variation selection on the product detail page.
 *
 * Architecture:
 * - The PHP template renders a visible thumbnail UI (.variation-thumbnails)
 *   and a hidden native WooCommerce <table class="variations"> with <select> elements.
 * - WooCommerce's own add-to-cart-variation.js watches `.variations select` for `change`
 *   events and does all the heavy lifting (price update, stock, found_variation, etc.).
 * - This script only syncs thumbnail clicks → hidden select → triggers `change`,
 *   and auto-selects the first variation on page load.
 * - The add-to-cart template is rendered twice (summary and quick order), so every
 *   form.variations_form is bound on its own.
 */

document.addEventListener('DOMContentLoaded', () => {
	const forms = document.querySelectorAll('form.variations_form');

	if (!forms.length) {
		return;
	}

	forms.forEach((form) => {
		// --- Click handler: thumbnail → update hidden select → trigger WC change ---
		form.addEventListener('click', (e) => {
			const thumb = e.target.closest('.variation-thumbnails__thumbnail');
			if (!thumb || !form.contains(thumb)) return;

			e.preventDefault();

			const group = thumb.closest('.variation-thumbnails__group');
			const attrName = group.dataset.attribute_name;
			const value = thumb.dataset.value;

			// Update selected state on thumbnails in this group.
			group.querySelectorAll('.variation-thumbnails__thumbnail').forEach((t) => {
				t.classList.remove('variation-thumbnails__thumbnail--selected');
				t.setAttribute('aria-pressed', 'false');
			});
			thumb.classList.add('variation-thumbnails__thumbnail--selected');
			thumb.setAttribute('aria-pressed', 'true');

			// Update the selected-name label without overwriting the "Geselecteerd:" prefix.
			const valueEl = group.querySelector('.variation-thumbnails__selected-value');
			if (valueEl) valueEl.textContent = thumb.getAttribute('title');

			// Sync with the hidden WooCommerce select and let WC handle everything else.
			const select = form.querySelector(`.variations select[name="${attrName}"]`);
			if (select) {
				select.value = value;
				select.dispatchEvent(new Event('change', { bubbles: true }));
			}
		});

		// --- Reset: always re-select the first thumbnail so a variant stays active ---
		form.addEventListener('reset_data', () => {
			autoSelect(form);
		});

		// --- Auto-select: trigger click on each group's pre-selected or first thumbnail ---
		autoSelect(form);

		repositionDescription(form);
	});

	bindHoverPreview();

	function autoSelect(form) {
		form.querySelectorAll('.variation-thumbnails__group').forEach((group) => {
			const preSelected = group.querySelector('.variation-thumbnails__thumbnail--selected');
			const toSelect = preSelected ?? group.querySelector('.variation-thumbnails__thumbnail');

			if (toSelect) {
				toSelect.click();
			}
		});
	}

	// --- Reposition variation description: move it below .woocommerce-variation-add-to-cart ---
	// WooCommerce fires show_variation as a jQuery event (.trigger()), which does NOT reach
	// native addEventListener. A MutationObserver on .single_variation is reliable instead.
	function repositionDescription(form) {
		const singleVariation = form.querySelector('.single_variation');
		const addToCartWrapper = form.querySelector('.woocommerce-variation-add-to-cart');

		if (!singleVariation || !addToCartWrapper) {
			return;
		}

		const observer = new MutationObserver(() => {
			const desc = singleVariation.querySelector('.woocommerce-variation-description');
			// Only act on a fresh description that hasn't been repositioned yet.
			if (desc && !desc.hasAttribute('data-repositioned')) {
				const existing = form.querySelector('.woocommerce-variation-description[data-repositioned]');
				if (existing) existing.remove();
				desc.dataset.repositioned = '';
				addToCartWrapper.after(desc);
			}
		});

		observer.observe(singleVariation, { childList: true, subtree: true });
	}

	// The preview image lives in the thumbnail, inside ancestors with
	// overflow: hidden. A fixed copy on document.body is not clipped by them.
	function bindHoverPreview() {
		const floating = document.createElement('img');
		floating.className = 'variation-thumbnails__preview variation-thumbnails__preview--floating';
		floating.alt = '';
		floating.setAttribute('aria-hidden', 'true');
		floating.addEventListener('load', positionPreview);
		document.body.appendChild(floating);

		const desktop = window.matchMedia('(min-width: 43em)');
		let activeThumb = null;

		document.addEventListener('mouseover', (e) => {
			const thumb = e.target.closest('.variation-thumbnails__thumbnail');
			if (!thumb || thumb === activeThumb) return;
			showPreview(thumb);
		});

		document.addEventListener('mouseout', (e) => {
			if (!activeThumb) return;
			const next = e.relatedTarget && e.relatedTarget.closest
				? e.relatedTarget.closest('.variation-thumbnails__thumbnail')
				: null;
			if (next) return;
			if (e.target.closest('.variation-thumbnails__thumbnail') === activeThumb) {
				hidePreview();
			}
		});

		document.addEventListener('focusin', (e) => {
			const thumb = e.target.closest('.variation-thumbnails__thumbnail');
			if (thumb) showPreview(thumb);
		});

		document.addEventListener('focusout', (e) => {
			const thumb = e.target.closest('.variation-thumbnails__thumbnail');
			if (!thumb || thumb !== activeThumb) return;
			const next = e.relatedTarget && e.relatedTarget.closest
				? e.relatedTarget.closest('.variation-thumbnails__thumbnail')
				: null;
			if (!next) hidePreview();
		});

		window.addEventListener('scroll', positionPreview, true);
		window.addEventListener('resize', () => {
			if (!desktop.matches) hidePreview();
			else positionPreview();
		});

		function showPreview(thumb) {
			if (!desktop.matches) return;
			const source = thumb.querySelector('.variation-thumbnails__preview:not(.variation-thumbnails__preview--floating)');
			if (!source || !source.getAttribute('src')) return;

			activeThumb = thumb;
			if (floating.src !== source.src) floating.src = source.src;
			floating.classList.add('is-visible');
			positionPreview();
		}

		function hidePreview() {
			activeThumb = null;
			floating.classList.remove('is-visible');
		}

		function positionPreview() {
			if (!activeThumb) return;

			const thumbRect = activeThumb.getBoundingClientRect();
			const list = activeThumb.closest('.variation-thumbnails__list');
			const listRect = list ? list.getBoundingClientRect() : thumbRect;
			const margin = 16;
			const fontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
			const width = Math.min(22 * fontSize, window.innerWidth - margin * 2);

			floating.style.width = `${width}px`;

			const height = floating.offsetHeight || width * 0.67;
			let left = listRect.left;
			left = Math.min(left, window.innerWidth - margin - width);
			left = Math.max(left, margin);

			let top = thumbRect.top - height - 8;
			if (top < margin) {
				top = Math.min(thumbRect.bottom + 8, window.innerHeight - margin - height);
			}

			floating.style.left = `${left}px`;
			floating.style.top = `${top}px`;
		}
	}
});
